import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../contexts/authContext';
import { useAlert } from '../../contexts/alertContext';
import { useTitle } from '../../hooks/titleProvider';
import PrimaryButton from '../ui/buttons/primaryButton';
import userService from '../../features/user/userService';
import { changeUserPassword } from '../../services/auth';
import { getVenues } from '../../features/venue/venueSlice';
import PhoneNumberInput from '../ui/forms/fields/phoneNumberInput';

const Profile = () => {
  const dispatch = useDispatch();
  const venues = useSelector((state) => state.venue.venues) || [];
  const loadingVenues = useSelector((state) => state.venue.loadingVenues);
  const { currentUserProfile, refreshCurrentUserProfile } = useAuth();
  const { showAlertError, showAlertSuccess } = useAlert();
  const { setTitle } = useTitle();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    homeClub: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const { control, setValue, watch } = useForm({
    defaultValues: {
      phone: ''
    }
  });
  const watchedPhone = watch('phone');

  const normalizeHomeClubValue = (value) => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'object') {
      const clubId = String(value.id || value.club || value.value || '').trim();
      if (clubId) {
        return clubId;
      }

      return String(value.name || '').trim();
    }

    return String(value).trim();
  };

  useEffect(() => {
    setTitle('Profile');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getVenues());
  }, [dispatch]);

  useEffect(() => {
    const firstClubEntry =
      currentUserProfile?.clubs && Array.isArray(currentUserProfile.clubs)
        ? currentUserProfile.clubs[0] || {}
        : {};

    const fallbackClub =
      currentUserProfile?.homeClub ||
      currentUserProfile?.homeClubId ||
      currentUserProfile?.clubId ||
      firstClubEntry?.club ||
      firstClubEntry?.clubId ||
      firstClubEntry?.venueId ||
      firstClubEntry?.venue ||
      firstClubEntry?.id ||
      '';

    setForm({
      firstName: currentUserProfile?.firstName || '',
      lastName: currentUserProfile?.lastName || '',
      phone: currentUserProfile?.phone || '',
      homeClub: normalizeHomeClubValue(currentUserProfile?.homeClub || fallbackClub),
      email: currentUserProfile?.email || ''
    });
  }, [currentUserProfile]);

  useEffect(() => {
    setValue('phone', String(form.phone || ''));
  }, [form.phone, setValue]);

  useEffect(() => {
    const normalizedPhone = String(watchedPhone || '');
    if (normalizedPhone !== String(form.phone || '')) {
      setForm((previous) => ({
        ...previous,
        phone: normalizedPhone
      }));
    }
  }, [watchedPhone]);

  const fullName = useMemo(() => {
    const firstName = String(form.firstName || '').trim();
    const lastName = String(form.lastName || '').trim();
    return `${firstName} ${lastName}`.trim();
  }, [form.firstName, form.lastName]);

  const sortedVenues = useMemo(() => {
    return [...venues].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [venues]);

  const selectedHomeClubValue = useMemo(() => {
    const currentValue = normalizeHomeClubValue(form.homeClub);
    if (!currentValue) {
      return '';
    }

    const byId = sortedVenues.find((venue) => String(venue?.id || '') === currentValue);
    if (byId) {
      return String(byId.id);
    }

    const byName = sortedVenues.find(
      (venue) =>
        String(venue?.name || '')
          .trim()
          .toLowerCase() === currentValue.toLowerCase()
    );

    return byName ? String(byName.id) : currentValue;
  }, [form.homeClub, sortedVenues]);

  const selectedVenue = useMemo(() => {
    return sortedVenues.find((venue) => String(venue?.id || '') === String(selectedHomeClubValue));
  }, [sortedVenues, selectedHomeClubValue]);

  const selectedHomeClubLabel = useMemo(() => {
    if (!selectedHomeClubValue) {
      return '';
    }

    if (selectedVenue?.name) {
      return String(selectedVenue.name);
    }

    const fallbackLabel = String(form.homeClub || '').trim();
    return fallbackLabel || 'Current club';
  }, [selectedHomeClubValue, selectedVenue, form.homeClub]);

  useEffect(() => {
    if (loadingVenues || !form.homeClub) {
      return;
    }

    if (selectedHomeClubValue && selectedHomeClubValue !== form.homeClub) {
      setForm((previous) => ({
        ...previous,
        homeClub: selectedHomeClubValue
      }));
    }
  }, [loadingVenues, selectedHomeClubValue, form.homeClub]);

  const onSaveProfile = async () => {
    if (saving) {
      return;
    }

    const firstName = String(form.firstName || '').trim();
    const lastName = String(form.lastName || '').trim();

    if (!firstName || !lastName) {
      showAlertError('First name and last name are required.');
      return;
    }

    try {
      setSaving(true);
      await userService.updateProfile({
        firstName,
        lastName,
        phone: String(form.phone || '').trim(),
        homeClub: String(form.homeClub || '').trim()
      });
      await refreshCurrentUserProfile();
      showAlertSuccess('Profile updated successfully.');
    } catch (error) {
      showAlertError(error?.response?.data || error?.message || 'Unable to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const onChangePassword = async () => {
    if (changingPassword) {
      return;
    }

    const currentPassword = String(passwordForm.currentPassword || '').trim();
    const newPassword = String(passwordForm.newPassword || '').trim();
    const confirmPassword = String(passwordForm.confirmPassword || '').trim();

    if (!currentPassword) {
      showAlertError('Current password is required.');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      showAlertError('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlertError('New password and confirm password do not match.');
      return;
    }

    if (newPassword === currentPassword) {
      showAlertError('New password must be different from current password.');
      return;
    }

    try {
      setChangingPassword(true);
      await changeUserPassword(currentPassword, newPassword);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      showAlertSuccess('Password updated successfully.');
    } catch (error) {
      const message = String(error?.message || '').toLowerCase();
      if (message.includes('wrong-password') || message.includes('invalid-credential')) {
        showAlertError('Current password is incorrect.');
      } else if (message.includes('requires-recent-login')) {
        showAlertError('Please sign in again, then retry changing your password.');
      } else {
        showAlertError('Unable to change password. Please try again.');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Edit Profile</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="First name"
                value={form.firstName}
                size="small"
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, firstName: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Last name"
                value={form.lastName}
                size="small"
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, lastName: event.target.value }))
                }
                fullWidth
              />
            </Stack>

            <TextField
              label="Email"
              value={form.email}
              size="small"
              disabled
              fullWidth
              helperText="Email cannot be changed here. Please contact support to update your email."
            />

            <PhoneNumberInput
              control={control}
              name="phone"
              label="Phone Number"
              defaultValue={form.phone || ''}
              helperText={''}
              error={false}
            />

            {!loadingVenues ? (
              <TextField
                label="Home club"
                select
                value={selectedHomeClubValue}
                size="small"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    const normalized = String(value || '').trim();
                    if (!normalized) {
                      return 'Select a club';
                    }

                    return selectedHomeClubLabel || 'Current club';
                  }
                }}
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, homeClub: event.target.value }))
                }
                fullWidth>
                <MenuItem value="">
                  <em>Select a club</em>
                </MenuItem>
                {selectedHomeClubValue && !selectedVenue ? (
                  <MenuItem value={selectedHomeClubValue}>Current club</MenuItem>
                ) : null}
                {sortedVenues.map((venue) => (
                  <MenuItem key={venue.id} value={String(venue.id)}>
                    {venue.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box
                sx={{
                  minHeight: 40,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading Clubs
                </Typography>
              </Box>
            )}

            <Box display="flex" alignItems="center" gap={1.5}>
              <PrimaryButton variant="contained" onClick={onSaveProfile} disabled={saving}>
                Save Profile
              </PrimaryButton>
              {saving ? <CircularProgress size={18} /> : null}
              <Typography variant="caption" color="text.secondary">
                {fullName || 'Profile'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6">Change Password</Typography>
            <Typography variant="body2" color="text.secondary">
              Update your password using your current password.
            </Typography>
            <TextField
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              size="small"
              onChange={(event) =>
                setPasswordForm((previous) => ({
                  ...previous,
                  currentPassword: event.target.value
                }))
              }
              fullWidth
            />
            <TextField
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              size="small"
              onChange={(event) =>
                setPasswordForm((previous) => ({ ...previous, newPassword: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={passwordForm.confirmPassword}
              size="small"
              onChange={(event) =>
                setPasswordForm((previous) => ({
                  ...previous,
                  confirmPassword: event.target.value
                }))
              }
              fullWidth
            />
            <Box display="flex" alignItems="center" gap={1.5}>
              <PrimaryButton
                variant="outlined"
                onClick={onChangePassword}
                disabled={changingPassword}>
                Update Password
              </PrimaryButton>
              {changingPassword ? <CircularProgress size={18} /> : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default Profile;
